import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useClerk } from '@clerk/clerk-react';
import { Button, ErrorNotice, LoadingState, Panel } from '@bcecloud/portal-ui';
import { getJson, errorMessage, apiErrorCode } from '@/lib/api';
import { EventsPage } from '@/pages/EventsPage';
import { EventDetailPage } from '@/pages/EventDetailPage';
import { AnalyticsTracker } from '@/components/AnalyticsTracker';
import { EventShippingPage } from '@/pages/event-shipping';
import { MaterialsEmailPage } from '@/pages/event-materials-email';
import { ContractTextPage } from '@/pages/event-contract-text';
import { ContractPreviewPage } from '@/pages/event-contract-preview';
import { ContractCountersignPage } from '@/pages/event-contract-countersign';
import { ShipmentsPage } from '@/pages/ShipmentsPage';
import { ShipmentDetailPage } from '@/pages/ShipmentDetailPage';
import { RecapsPage } from '@/pages/RecapsPage';
import { RecapDetailPage } from '@/pages/RecapDetailPage';
import { ProgramsPage, ProgramDetailPage } from '@/pages/ProgramsPage';
import { SeasonTagsPage } from '@/pages/SeasonTagsPage';
import { ProgramPairingsPage } from '@/pages/ProgramPairingsPage';
import { PeoplePage } from '@/pages/PeoplePage';
import { ProfilePage } from '@/pages/ProfilePage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { NewOrganizationPage } from '@/pages/NewOrganizationPage';
import { OrganizationDetailPage } from '@/pages/OrganizationDetailPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AdoptionPage } from '@/pages/AdoptionPage';
import { McpUsagePage } from '@/pages/McpUsagePage';
import { MCP_USAGE_PATH } from '@/lib/mcp-usage';
import { LeadsPage } from '@/pages/LeadsPage';
import { LeadDetailPage } from '@/pages/LeadDetailPage';
import { SubmissionsPage } from '@/pages/SubmissionsPage';
import { PagesAnalyticsPage } from '@/pages/PagesAnalyticsPage';
import { PpcAnalyticsPage } from '@/pages/PpcAnalyticsPage';
import { PricingAnalyticsPage } from '@/pages/PricingAnalyticsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { ExpensesPage } from '@/pages/ExpensesPage';
import { PnlPage } from '@/pages/PnlPage';
import { ProgramProfitabilityPage } from '@/pages/ProgramProfitabilityPage';
import { PROGRAMS_ROUTE } from '@/lib/program-profitability';
import { CashFlowPage } from '@/pages/CashFlowPage';
import { CashReceivablesPage } from '@/pages/CashReceivablesPage';
import { CashAssumptionsPage } from '@/pages/CashAssumptionsPage';
import { ForecastAccuracyPage } from '@/pages/ForecastAccuracyPage';
import { FacilitatorsPage } from '@/pages/FacilitatorsPage';
import { FacilitatorDetailPage } from '@/pages/FacilitatorDetailPage';
import { FACILITATORS_PATH } from '@/lib/facilitator-spend';
import { EventPnlPage } from '@/pages/EventPnlPage';
import { EVENT_PNL_PATH } from '@/lib/event-pnl';
import { PageDetailPage } from '@/pages/PageDetailPage';
import { PageTypesAnalyticsPage } from '@/pages/PageTypesAnalyticsPage';
import { PageTestsAnalyticsPage } from '@/pages/PageTestsAnalyticsPage';
import { PageTypeDetailPage } from '@/pages/PageTypeDetailPage';
import { KeywordsAnalyticsPage } from '@/pages/KeywordsAnalyticsPage';
import { EventsAnalyticsPage } from '@/pages/EventsAnalyticsPage';
import { TargetedAnalyticsPage } from '@/pages/TargetedAnalyticsPage';
import { TargetedAnalysisPage } from '@/pages/TargetedAnalysisPage';
import { SALES_PATH, SalesPage } from '@/pages/SalesPage';
import { SalesRepPage } from '@/pages/SalesRepPage';
import { SalesStalePage } from '@/pages/SalesStalePage';
import { LEAD_PANEL_PATH, LeadPanelPage } from '@/pages/LeadPanelPage';
import { SalesOpportunityPage } from '@/pages/SalesOpportunityPage';
import { SalesAccountsPage } from '@/pages/SalesAccountsPage';
import { SalesAccountPage } from '@/pages/SalesAccountPage';
import { SalesContactsPage } from '@/pages/SalesContactsPage';
import { SalesContactPage } from '@/pages/SalesContactPage';
import { SALES_WORKSPACE_PATH } from '@/lib/sales-workspace';
import { SALES_ACCOUNTS_PATH, SALES_CONTACTS_PATH } from '@/lib/sales-data';
import { REPEAT_CLIENTS_PATH } from '@/lib/repeat-clients';
import { RepeatClientsPage } from '@/pages/RepeatClientsPage';
import { OutreachPlaybookPage } from '@/pages/OutreachPlaybookPage';
import { OutreachEmailPage } from '@/pages/OutreachEmailPage';
import { RepeatClientPage } from '@/pages/RepeatClientPage';
import { REPEAT_CLIENT_ROUTE } from '@/lib/repeat-clients';
import { OUTREACH_EMAIL_ROUTE } from '@/lib/outreach-email';
import { OUTREACH_PLAYBOOK_PATH } from '@/lib/outreach-playbook';
import { PortalDefaultsPage } from '@/pages/PortalDefaultsPage';
import { OutreachTemplatesPage } from '@/pages/OutreachTemplatesPage';
import { OutreachTemplatePage } from '@/pages/OutreachTemplatePage';
import {
  OUTREACH_TEMPLATES_PATH,
  OUTREACH_TEMPLATE_ROUTE,
  OUTREACH_TEMPLATE_SCOPE,
  TEAM_OUTREACH_TEMPLATE_ROUTE,
} from '@/lib/outreach-templates';
import { PromoBannersPage } from '@/pages/PromoBannersPage';
import { ResourcesPage } from '@/pages/ResourcesPage';
import { ResourceEditorPage } from '@/pages/ResourceEditorPage';
import { PortalAccessPage } from '@/pages/PortalAccessPage';
import { PricingTemplatesPage, PricingTemplateDetailPage } from '@/pages/PricingTemplatesPage';
import { StaffContactsPage, StaffContactPage, StaffContactEditPage } from '@/pages/StaffContactsPage';
import { CharitiesPage, CharityDetailPage } from '@/pages/CharitiesPage';
import { PackingListPrintPage } from '@/pages/event-packing-list';
import { SupplyItemPage } from '@/pages/settings-supply-item';
import { RolePage } from '@/pages/RolePage';
import { HelpPage } from '@/pages/HelpPage';
import { HelpArticlePage } from '@/pages/HelpArticlePage';
import { ReleasePage, WhatsNewPage } from '@/pages/WhatsNewPage';
import { DevTrackerPage } from '@/pages/DevTrackerPage';
import { TemplatesPage, TemplateDetailPage } from '@/pages/TemplatesPage';
import { TemplateUsagePage } from '@/pages/TemplateUsagePage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { EnquiryDetailPage } from '@/pages/EnquiryDetailPage';
import { CampaignDetailPage } from '@/pages/CampaignDetailPage';
import { BookingInquiryPage } from '@/pages/BookingInquiryPage';
import { AdminShell } from '@/components/AdminShell';
import { RequireFeature } from '@/components/RequireFeature';
import { APP_FEATURE } from '@/lib/app-features';
import { RequirePermission } from '@/components/RequirePermission';
import { RedirectKeepingSearch } from '@/components/RedirectKeepingSearch';
import { ACCOUNTING_PATH, LEGACY_ACCOUNTING_PATH } from '@/lib/finance';
import {
  PERMISSION_MANAGE_CLIENT_PORTALS,
  PERMISSION_VIEW_ANALYTICS,
  PERMISSION_GENERATE_PROPOSALS,
  PERMISSION_WORK_REPEAT_CLIENTS,
  PERMISSION_VIEW_DEV_TRACKER,
  PERMISSION_VIEW_SALES_DASHBOARD,
  PERMISSION_VIEW_ACCOUNTING,
  PERMISSION_MANAGE_CONTRACTS,
  PERMISSION_MANAGE_PAYMENTS,
  PERMISSION_PROCESS_INVOICES,
} from '@/lib/events';
import { ProposalsPage } from '@/pages/ProposalsPage';
import { ContractsPage } from '@/pages/ContractsPage';
import { PaymentsPage } from '@/pages/PaymentsPage';
import { InvoicesPage } from '@/pages/InvoicesPage';
import { InvoiceDetailPage } from '@/pages/InvoiceDetailPage';
import { INVOICES_PATH } from '@/lib/invoices';
import { ProposalEditorPage } from '@/pages/ProposalEditorPage';
import { PageLibraryPage } from '@/pages/PageLibraryPage';
import { SkillsPage } from '@/pages/assistant/SkillsPage';
import { SkillViewPage } from '@/pages/assistant/SkillViewPage';
import { SkillEditPage } from '@/pages/assistant/SkillEditPage';
import { MemoryPage } from '@/pages/assistant/MemoryPage';
import { MemoryViewPage } from '@/pages/assistant/MemoryViewPage';
import { MemoryEditPage } from '@/pages/assistant/MemoryEditPage';
import { ChatsPage } from '@/pages/assistant/ChatsPage';
import { ChatViewPage } from '@/pages/assistant/ChatViewPage';
import { SchedulesPage } from '@/pages/assistant/SchedulesPage';
import { GapsPage } from '@/pages/assistant/GapsPage';
import { AttachmentPage } from '@/pages/AttachmentPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { ContactPage } from '@/pages/ContactPage';
import { ContactLookupPage } from '@/pages/ContactLookupPage';
import { CitiesPage, PlaceEventsPage, VenuesPage } from '@/pages/PlacesPage';
import { AccountPage, AccountsPage } from '@/pages/AccountsPage';
import { ScheduleEditPage } from '@/pages/assistant/ScheduleEditPage';
import { ScheduleViewPage } from '@/pages/assistant/ScheduleViewPage';
import { FEATURES } from '@/lib/flags';

/** The staff identity the API resolved for this session. */
interface StaffMe {
  readonly _id: string;
  readonly email: string;
  readonly role: string;
  readonly firstName?: string;
  readonly lastName?: string;
}

/** V3 roles admitted to the staff app — internal staff plus contractors,
 * who sign in to work their own assigned events. */
const STAFF_ROLES = new Set(['contractor', 'internal', 'admin', 'superadmin']);

/**
 * Application root.
 *
 * Probes staff standing once before rendering anything: a valid Clerk session
 * is necessary but not sufficient here, and "signed in but not a BCE admin"
 * deserves one clear screen rather than every page erroring separately.
 *
 * @returns The admin application.
 */
export default function App(): React.ReactElement {
  const clerk = useClerk();

  const me = useQuery({
    queryKey: ['staff-me'],
    queryFn: ({ signal }) => getJson<StaffMe>('/users/me', undefined, signal),
    retry: (failureCount, error) =>
      // 403 is an answer, not a glitch. Retrying cannot change it.
      apiErrorCode(error) === 'FORBIDDEN' ? false : failureCount < 2,
  });

  if (me.isPending) {
    return (
      <main className="grid min-h-screen place-items-center bg-portal-bg">
        <LoadingState label="Checking your access…" />
      </main>
    );
  }

  if (me.isError) {
    const denied = apiErrorCode(me.error) === 'FORBIDDEN';
    return (
      <main className="grid min-h-screen place-items-center bg-portal-bg p-6">
        <Panel className="max-w-[440px] p-6">
          {denied ? (
            <>
              <h1 className="text-portal-card-title font-bold">
                This tool is for the BCE team
              </h1>
              <p className="mt-2 text-portal-small text-portal-muted">
                You are signed in, but this account does not have access — most likely it was
                deactivated. Ask a BCE team admin, then sign in again.
              </p>
            </>
          ) : (
            <ErrorNotice title="Could not check your access" message={errorMessage(me.error)} />
          )}
          <Button variant="secondary" className="mt-5" onClick={() => void clerk.signOut()}>
            Sign out
          </Button>
        </Panel>
      </main>
    );
  }

  // The staff Clerk instance means everyone here should hold a staff role,
  // but a client-role account slipping through must still see a refusal
  // rather than an admin tool.
  if (!STAFF_ROLES.has(me.data.role)) {
    return (
      <main className="grid min-h-screen place-items-center bg-portal-bg p-6">
        <Panel className="max-w-[440px] p-6">
          <h1 className="text-portal-card-title font-bold">This tool is for the BCE team</h1>
          <p className="mt-2 text-portal-small text-portal-muted">
            Your account ({me.data.email}) does not have staff access.
          </p>
          <Button variant="secondary" className="mt-5" onClick={() => void clerk.signOut()}>
            Sign out
          </Button>
        </Panel>
      </main>
    );
  }

  return (
    <AdminShell>
      <AnalyticsTracker viewer={me.data} />
      <Routes>
        <Route path="/" element={<Navigate to="/events" replace />} />
        {/* The Search funnel page folded into PPC (Oct 2026); old bookmarks land there. */}
        <Route path="/analytics/funnel" element={<Navigate to="/analytics/ppc" replace />} />
        {/* Client portals' staff door lands here; the sign-in gate above this
            router covers it, and the API's role check gates the mint itself. */}
        <Route path="/portal-access" element={<PortalAccessPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:eventId" element={<EventDetailPage />} />
        <Route path="/events/:eventId/shipping" element={<EventShippingPage />} />
        <Route path="/events/:eventId/packing-list/print" element={<RequireFeature feature={APP_FEATURE.materials}><PackingListPrintPage /></RequireFeature>} />
        <Route path="/events/:eventId/shipments/email" element={<MaterialsEmailPage />} />
        <Route path="/events/:eventId/contracts/:contractId/text" element={<ContractTextPage />} />
        <Route path="/events/:eventId/contracts/:contractId/preview" element={<ContractPreviewPage />} />
        <Route path="/events/:eventId/contracts/:contractId/countersign" element={<ContractCountersignPage />} />
        <Route path="/shipments" element={<ShipmentsPage />} />
        <Route path="/shipments/:shipmentId" element={<ShipmentDetailPage />} />
        <Route path="/recaps" element={<RecapsPage />} />
        <Route path="/recaps/:recapId" element={<RecapDetailPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/programs/:programId" element={<ProgramDetailPage />} />
        <Route path="/season-tags" element={<SeasonTagsPage />} />
        <Route path="/program-pairings" element={<ProgramPairingsPage />} />
        <Route path="/people" element={<PeoplePage />} />
        <Route path="/people/:userId" element={<ProfilePage />} />
        <Route path="/staff-contacts" element={<StaffContactsPage />} />
        <Route path="/staff-contacts/new" element={<StaffContactEditPage />} />
        <Route path="/staff-contacts/:contactId" element={<StaffContactPage />} />
        <Route path="/staff-contacts/:contactId/edit" element={<StaffContactEditPage />} />
        {/* Internal-only; the API 403s everyone else. */}
        <Route path="/charities" element={<CharitiesPage />} />
        <Route path="/charities/:charityId" element={<CharityDetailPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* Role-gated server-side (admin/superadmin); nav hides it otherwise. */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/roles/:roleId" element={<RolePage />} />
        <Route path="/settings/supplies/new" element={<RequireFeature feature={APP_FEATURE.materials}><SupplyItemPage /></RequireFeature>} />
        <Route path="/settings/supplies/:itemId" element={<RequireFeature feature={APP_FEATURE.materials}><SupplyItemPage /></RequireFeature>} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/:area/:slug" element={<HelpArticlePage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/whats-new/:slug" element={<ReleasePage />} />
        {/* Named people only: the view_dev_tracker grant (the API enforces the same). */}
        <Route
          path="/development"
          element={
            <RequirePermission permission={PERMISSION_VIEW_DEV_TRACKER}>
              <DevTrackerPage />
            </RequirePermission>
          }
        />
        <Route path="/templates" element={<RequireFeature feature={APP_FEATURE.noteTemplates}><TemplatesPage /></RequireFeature>} />
        <Route path="/templates/:templateId" element={<RequireFeature feature={APP_FEATURE.noteTemplates}><TemplateDetailPage /></RequireFeature>} />
        <Route path="/templates/:templateId/usage" element={<RequireFeature feature={APP_FEATURE.noteTemplates}><TemplateUsagePage /></RequireFeature>} />
        <Route path="/adoption" element={<AdoptionPage />} />
        {/* Proposals — scoped by the generate_proposals permission (the API
            enforces the same grant; superadmins hold it implicitly). */}
        {/* Sales → My email templates: each rep's outreach templates, on the Repeat clients grant. */}
        <Route
          path={OUTREACH_TEMPLATES_PATH}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <OutreachTemplatesPage />
            </RequirePermission>
          }
        />
        {/* One template (or `new`) on its own page; the team's under /team. */}
        <Route
          path={OUTREACH_TEMPLATE_ROUTE}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <OutreachTemplatePage scope={OUTREACH_TEMPLATE_SCOPE.personal} />
            </RequirePermission>
          }
        />
        <Route
          path={TEAM_OUTREACH_TEMPLATE_ROUTE}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <OutreachTemplatePage scope={OUTREACH_TEMPLATE_SCOPE.team} />
            </RequirePermission>
          }
        />
        <Route
          path="/proposals"
          element={
            <RequirePermission permission={PERMISSION_GENERATE_PROPOSALS}>
              <RequireFeature feature={APP_FEATURE.proposals}>
                <ProposalsPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/page-library"
          element={
            <RequirePermission permission={PERMISSION_GENERATE_PROPOSALS}>
              <RequireFeature feature={APP_FEATURE.proposals}>
                <RequireFeature feature={APP_FEATURE.pageLibrary}>
                  <PageLibraryPage />
                </RequireFeature>
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/proposals/:id"
          element={
            <RequirePermission permission={PERMISSION_GENERATE_PROPOSALS}>
              <RequireFeature feature={APP_FEATURE.proposals}>
                <ProposalEditorPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        {/* Sales → Lead Control Panel — internal staff (the API 403s anyone
            else); its assign/disqualify actions follow manage_lead_assignment. */}
        <Route path={LEAD_PANEL_PATH} element={<LeadPanelPage />} />
        {/* Sales → My day — dark: the API admits only SALES_WORKSPACE_USER_IDS
            (superadmins not included); anyone else lands as on an unknown URL. */}
        {/* Sales → Repeat clients: its own grant, work_repeat_clients (ships apart from My day). */}
        <Route
          path={OUTREACH_PLAYBOOK_PATH}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <OutreachPlaybookPage />
            </RequirePermission>
          }
        />
        <Route
          path={REPEAT_CLIENT_ROUTE}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <RepeatClientPage />
            </RequirePermission>
          }
        />
        <Route
          path={OUTREACH_EMAIL_ROUTE}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <OutreachEmailPage />
            </RequirePermission>
          }
        />
        <Route
          path={REPEAT_CLIENTS_PATH}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <RepeatClientsPage />
            </RequirePermission>
          }
        />
        <Route
          path={`${REPEAT_CLIENTS_PATH}/rep/:repId`}
          element={
            <RequirePermission permission={PERMISSION_WORK_REPEAT_CLIENTS}>
              <RepeatClientsPage />
            </RequirePermission>
          }
        />
        {/* My day was retired for Repeat clients (Thomas, 2026-10-24); old links land there. */}
        <Route path={SALES_WORKSPACE_PATH} element={<Navigate to={REPEAT_CLIENTS_PATH} replace />} />
        <Route path={`${SALES_WORKSPACE_PATH}/rep/:repId`} element={<Navigate to={REPEAT_CLIENTS_PATH} replace />} />
        <Route path={`${SALES_WORKSPACE_PATH}/opportunities/:opportunityId`} element={<SalesOpportunityPage />} />
        {/* Sales accounts and contacts — dark the same way (no nav entry yet). */}
        <Route path={SALES_ACCOUNTS_PATH} element={<SalesAccountsPage />} />
        <Route path={`${SALES_ACCOUNTS_PATH}/:accountId`} element={<SalesAccountPage />} />
        <Route path={SALES_CONTACTS_PATH} element={<SalesContactsPage />} />
        <Route path={`${SALES_CONTACTS_PATH}/:contactId`} element={<SalesContactPage />} />
        {/* Win-back was folded into Repeat clients (Thomas, 2026-10-24). */}
        <Route path="/analytics/win-back" element={<Navigate to={REPEAT_CLIENTS_PATH} replace />} />
        {/* Sales → Contracts — every event contract, scoped by the
            manage_contracts permission (the API enforces the same grant). */}
        <Route
          path="/contracts"
          element={
            <RequirePermission permission={PERMISSION_MANAGE_CONTRACTS}>
              <ContractsPage />
            </RequirePermission>
          }
        />
        {/* Sales → Payments — what contracted events are owed, scoped by the
            manage_payments permission (the API enforces the same grant). */}
        <Route
          path="/payments"
          element={
            <RequirePermission permission={PERMISSION_MANAGE_PAYMENTS}>
              <PaymentsPage />
            </RequirePermission>
          }
        />
        {/* Tools → Invoices — vendor invoices read into expense lines, scoped
            by the process_invoices permission (the API enforces the same grant). */}
        {(
          [
            [INVOICES_PATH, <InvoicesPage />],
            [`${INVOICES_PATH}/:id`, <InvoiceDetailPage />],
          ] as const
        ).map(([path, element]) => (
          <Route
            key={path}
            path={path}
            element={<RequirePermission permission={PERMISSION_PROCESS_INVOICES}>{element}</RequirePermission>}
          />
        ))}
        {/* The analytics surface (management reporting, prospect PII), gated
            on the viewAnalytics permission (the API enforces it too). */}
        {(
          [
            ['/leads', <LeadsPage />],
            ['/leads/:leadId', <LeadDetailPage />],
            ['/submissions', <SubmissionsPage />],
            ['/analytics/events', <EventsAnalyticsPage />],
            ['/analytics/pages', <PagesAnalyticsPage />],
            ['/analytics/pages/detail', <PageDetailPage />],
            ['/analytics/page-types', <PageTypesAnalyticsPage />],
            ['/analytics/page-types/:type', <PageTypeDetailPage />],
            ['/analytics/page-tests', <PageTestsAnalyticsPage />],
            ['/analytics/keywords', <KeywordsAnalyticsPage />],
            ['/analytics/ppc', <PpcAnalyticsPage />],
            ['/analytics/pricing', <PricingAnalyticsPage />],
            ['/analytics/targeted', <TargetedAnalyticsPage />],
            ['/analytics/targeted/:slug', <TargetedAnalysisPage />],
            [MCP_USAGE_PATH, <McpUsagePage />],
          ] as const
        ).map(([path, element]) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission permission={PERMISSION_VIEW_ANALYTICS}>
                {element}
              </RequirePermission>
            }
          />
        ))}
        {/* Analytics → Accounting: the cash forecast pages, expenses, the P&L
            and the Company Analyst reports (which carry spend), gated on
            view_accounting (the API enforces it too). */}
        {(
          [
            ['/reports', <ReportsPage />],
            [ACCOUNTING_PATH.cashFlow, <CashFlowPage />],
            [ACCOUNTING_PATH.receivables, <CashReceivablesPage />],
            [ACCOUNTING_PATH.assumptions, <CashAssumptionsPage />],
            [ACCOUNTING_PATH.forecastAccuracy, <ForecastAccuracyPage />],
            [ACCOUNTING_PATH.expenses, <ExpensesPage />],
            [ACCOUNTING_PATH.pnl, <PnlPage />],
            [PROGRAMS_ROUTE, <ProgramProfitabilityPage />],
            [FACILITATORS_PATH.list, <FacilitatorsPage />],
            [FACILITATORS_PATH.person, <FacilitatorDetailPage />],
            [EVENT_PNL_PATH, <EventPnlPage />],
          ] as const
        ).map(([path, element]) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission permission={PERMISSION_VIEW_ACCOUNTING}>
                {element}
              </RequirePermission>
            }
          />
        ))}
        <Route
          path={LEGACY_ACCOUNTING_PATH.expenses}
          element={<RedirectKeepingSearch to={ACCOUNTING_PATH.expenses} />}
        />
        <Route path={LEGACY_ACCOUNTING_PATH.pnl} element={<RedirectKeepingSearch to={ACCOUNTING_PATH.pnl} />} />
        {/* Analytics → Sales: Bookings and the sales dashboard in one page,
            open to either permission (the API enforces it too). The old
            /bookings link lands there. */}
        <Route
          path={SALES_PATH}
          element={
            <RequirePermission permission={[PERMISSION_VIEW_SALES_DASHBOARD, PERMISSION_VIEW_ANALYTICS]}>
              <SalesPage />
            </RequirePermission>
          }
        />
        <Route path="/bookings" element={<RedirectKeepingSearch to={SALES_PATH} />} />
        {/* Its stale review queue and rep pages stay on view_sales_dashboard. */}
        {(
          [
            ['/analytics/sales/stale', <SalesStalePage />],
            ['/analytics/sales/:ownerId', <SalesRepPage />],
          ] as const
        ).map(([path, element]) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission permission={PERMISSION_VIEW_SALES_DASHBOARD}>
                {element}
              </RequirePermission>
            }
          />
        ))}
        {/* The client-portal administration surface, gated on the
            manageClientPortals permission (the API enforces it too). */}
        {(
          [
            ['/organizations', <OrganizationsPage />],
            ['/organizations/new', <NewOrganizationPage />],
            ['/organizations/:organizationId', <OrganizationDetailPage />],
            ['/organizations/:organizationId/enquiries/:inquiryId', <EnquiryDetailPage />],
            ['/organizations/:organizationId/requests/:bookingId', <BookingInquiryPage />],
            ['/campaigns/:campaignId', <CampaignDetailPage />],
            ['/campaigns/:campaignId/contracts/:contractId/text', <ContractTextPage />],
            ['/campaigns/:campaignId/contracts/:contractId/preview', <ContractPreviewPage />],
            ['/campaigns/:campaignId/contracts/:contractId/countersign', <ContractCountersignPage />],
            ['/organizations/:organizationId/agreement-requests/:requestId/contracts/:contractId/text', <ContractTextPage />],
            ['/organizations/:organizationId/agreement-requests/:requestId/contracts/:contractId/preview', <ContractPreviewPage />],
            ['/organizations/:organizationId/agreement-requests/:requestId/contracts/:contractId/countersign', <ContractCountersignPage />],
            ['/portal-defaults', <PortalDefaultsPage />],
            ['/promo-banners', <PromoBannersPage />],
            // The Resource center (Roy's portal list, item 13).
            ['/resources', <ResourcesPage />],
            ['/resources/new', <ResourceEditorPage />],
            ['/resources/:resourceId', <ResourceEditorPage />],
            ['/pricing-templates', <PricingTemplatesPage />],
            ['/pricing-templates/:templateId', <PricingTemplateDetailPage />],
          ] as const
        ).map(([path, element]) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission permission={PERMISSION_MANAGE_CLIENT_PORTALS}>
                {element}
              </RequirePermission>
            }
          />
        ))}
        {FEATURES.assistant ? (
          <>
            <Route path="/assistant/skills" element={<SkillsPage />} />
            <Route path="/assistant/skills/new" element={<SkillEditPage />} />
            <Route path="/assistant/skills/:scope/:name" element={<SkillViewPage />} />
            <Route path="/assistant/skills/:scope/:name/edit" element={<SkillEditPage />} />
            <Route path="/assistant/memory" element={<MemoryPage />} />
            <Route path="/assistant/memory/:memoryId" element={<MemoryViewPage />} />
            <Route path="/assistant/memory/:memoryId/edit" element={<MemoryEditPage />} />
            <Route path="/assistant/chats" element={<ChatsPage />} />
            <Route path="/assistant/chats/:chatId" element={<ChatViewPage />} />
            <Route path="/assistant/schedules" element={<SchedulesPage />} />
            <Route path="/assistant/gaps" element={<GapsPage />} />
            <Route path="/attachments/:id" element={<AttachmentPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/cities" element={<CitiesPage />} />
            <Route path="/venues" element={<VenuesPage />} />
            <Route path="/places/events" element={<PlaceEventsPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/accounts/:sfId" element={<AccountPage />} />
            <Route path="/contacts/by-email/:email" element={<ContactLookupPage />} />
            <Route path="/contacts/:id" element={<ContactPage />} />
            <Route path="/assistant/schedules/new" element={<ScheduleEditPage />} />
            <Route path="/assistant/schedules/:scheduleId" element={<ScheduleViewPage />} />
            <Route path="/assistant/schedules/:scheduleId/edit" element={<ScheduleEditPage />} />
          </>
        ) : null}
        <Route path="*" element={<Navigate to="/events" replace />} />
      </Routes>
    </AdminShell>
  );
}
